/**
 * EPI List Page
 * List and manage EPIs (Equipamentos de Proteção Individual)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  Typography,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Cancel as DeactivateIcon,
  CheckCircle as ActivateIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
} from '@mui/icons-material';
import {
  ActionButton,
  PageShell,
  DataTable,
  type DataTableColumn,
  DeleteWithDependenciesDialog,
  RouteActionButton,
} from '../../components';
import { pode } from '../../authorization/pode';
import { useAcao, useControle } from '../../authorization/useVeredito';
import { epiService } from '../../services/epiService';
import { Epi, EpiDependencies, EpiFilters, EpiStatus } from '../../types/epi';
import { dependencyItems } from '../../utils/dependencyItems';
import { EPIFiltersComponent, EpiFiltersData } from './components/EPIFilters';
import EPIImportDialog from './components/EPIImportDialog';
import { TipoDeRiscoChip } from './components/TipoDeRiscoChip';
import { CaStatusChip } from './components/CaStatusChip';
import { useDeleteWithDependencies } from '../../hooks/useDeleteWithDependencies';

const columns: DataTableColumn<Epi>[] = [
  {
    id: 'nome',
    label: 'Nome',
    primary: true,
    render: (epi) => (
      <Box>
        <Typography variant="body2" fontWeight="medium">
          {epi.nome}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {epi.risco}
        </Typography>
      </Box>
    ),
  },
  {
    id: 'tipoDeRisco',
    label: 'Tipo de Risco',
    render: (epi) => <TipoDeRiscoChip tipo={epi.tipoDeRisco} />,
  },
  {
    id: 'ca',
    label: 'C.A.',
    render: (epi) => (
      <Typography variant="body2" fontFamily="monospace">
        {epi.metodo}
      </Typography>
    ),
  },
  {
    id: 'validade',
    label: 'Validade',
    render: (epi) => <CaStatusChip fimValidade={epi.fimValidade} />,
  },
  {
    id: 'quantidade',
    label: 'Qtd',
    render: (epi) =>
      epi.quantidade !== null && epi.quantidade !== undefined ? (
        <Typography variant="body2">
          {epi.quantidade} {epi.unidade || ''}
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      ),
  },
  {
    id: 'status',
    label: 'Status',
    render: (epi) => (
      <Chip
        label={epi.ativo === EpiStatus.ATIVO ? 'Ativo' : 'Inativo'}
        size="small"
        color={epi.ativo === EpiStatus.ATIVO ? 'success' : 'default'}
      />
    ),
  },
];

export const EPIListPage: React.FC = () => {
  const navigate = useNavigate();
  const editar = useControle('/epis/:id/edit', pode('epi.update'), 'Editar');
  const ativar = useAcao('epi.update', 'Ativar');
  const desativar = useAcao('epi.update', 'Desativar');
  const excluir = useAcao('epi.delete', 'Excluir');
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [epis, setEpis] = useState<Epi[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [apiFilters, setApiFilters] = useState<EpiFilters>({});

  const loadEpis = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...apiFilters,
      };

      const response = await epiService.getEpis(params);
      setEpis(response.epis);
      setTotal(response.total);
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error.message ?? 'Erro ao carregar EPIs');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, apiFilters]);

  useEffect(() => {
    loadEpis();
  }, [loadEpis]);

  const handleFilter = useCallback((data: EpiFiltersData) => {
    const filters: EpiFilters = {};

    if (data.searchTerm) filters.search = data.searchTerm;
    if (data.tipoDeRisco) filters.tipoDeRisco = data.tipoDeRisco as EpiFilters['tipoDeRisco'];
    if (data.status) filters.ativo = data.status as EpiFilters['ativo'];
    if (data.caExpired) filters.caExpired = true;
    if (data.caExpiringSoon) filters.caExpiringSoon = true;

    setApiFilters(filters);
    setPage(0);
  }, []);

  const { deleteTarget, deleteDependencies, handleDeleteClick, closeDeleteDialog, handleDeleteConfirm } =
    useDeleteWithDependencies<Epi, EpiDependencies>({
      checkDependencies: (id) => epiService.checkDependencies(id),
      deleteEntity: (id) => epiService.deleteEpi(id),
      checkErrorMessage: 'Erro ao verificar vínculos do EPI',
      deleteErrorMessage: 'Erro ao excluir EPI',
      onError: setError,
      onDeleted: loadEpis,
    });

  const handleActivate = async (id: number) => {
    try {
      await epiService.activateEpi(id);
      loadEpis();
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error.message ?? 'Erro ao ativar EPI');
    }
  };

  const handleDeactivate = async (id: number) => {
    try {
      await epiService.deactivateEpi(id);
      loadEpis();
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error.message ?? 'Erro ao desativar EPI');
    }
  };

  const handleEdit = (epi: Epi) => {
    navigate(`/epis/${epi.id}/edit`);
  };

  const handleExport = async () => {
    try {
      const blob = await epiService.exportEpis();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'epis-export.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error.message ?? 'Erro ao exportar EPIs');
    }
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'EPIs' },
      ]}
      title="Gestão de EPIs"
      subtitle="Gerencie os Equipamentos de Proteção Individual"
      actions={
        <>
          <ActionButton
            acao="epi.read"
            rotulo="Exportar"
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={handleExport}
          >
            Exportar
          </ActionButton>
          <ActionButton
            acao="epi.import"
            rotulo="Importar"
            variant="outlined"
            startIcon={<ImportIcon />}
            onClick={() => setImportDialogOpen(true)}
          >
            Importar
          </ActionButton>
          <RouteActionButton
            para="/epis/new"
            exige={pode('epi.create')}
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
          >
            Novo EPI
          </RouteActionButton>
        </>
      }
    >
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <EPIFiltersComponent onFilter={handleFilter} loading={loading} total={total} />
      </Box>

      <DataTable
        columns={columns}
        rows={epis}
        getRowId={(epi) => epi.id}
        loading={loading}
        emptyState={{
          title: 'Nenhum EPI encontrado',
          description:
            'EPIs são registrados nesta tela e depois vinculados como proteção dos riscos ocupacionais.',
        }}
        actions={[
          {
            icon: <ActivateIcon fontSize="small" />,
            color: 'success',
            onClick: (epi) => handleActivate(epi.id),
            hidden: (epi) => epi.ativo !== EpiStatus.INATIVO,
            ...ativar,
          },
          {
            icon: <DeactivateIcon fontSize="small" />,
            color: 'warning',
            onClick: (epi) => handleDeactivate(epi.id),
            hidden: (epi) => epi.ativo !== EpiStatus.ATIVO,
            ...desativar,
          },
          {
            icon: <EditIcon fontSize="small" />,
            onClick: handleEdit,
            ...editar,
          },
          {
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            onClick: handleDeleteClick,
            ...excluir,
          },
        ]}
        pagination={{
          page,
          rowsPerPage,
          totalCount: total,
          onPageChange: setPage,
          onRowsPerPageChange: (value) => {
            setRowsPerPage(value);
            setPage(0);
          },
        }}
      />

      <EPIImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImportComplete={() => {
          setImportDialogOpen(false);
          loadEpis();
        }}
      />

      <DeleteWithDependenciesDialog
        open={Boolean(deleteTarget)}
        loading={deleteDependencies === null}
        blocked={Boolean(deleteDependencies?.hasDependencies)}
        title="Excluir EPI"
        confirmText="Tem certeza que deseja excluir este EPI?"
        prerequisite={{
          title: 'Este EPI está em uso',
          reason: 'EPIs com aplicações ativas não podem ser excluídos — remova as aplicações primeiro.',
          items: dependencyItems(deleteDependencies?.details),
          resolve: {
            label: 'Ver aplicações',
            permission: 'epi.update',
            mode: 'navigate',
            route: `/risk-applications?epiId=${deleteTarget?.id}`,
          },
          deniedHint: 'Peça a um administrador para remover as aplicações deste EPI antes de excluí-lo.',
        }}
        onClose={closeDeleteDialog}
        onConfirm={handleDeleteConfirm}
      />
    </PageShell>
  );
};

export default EPIListPage;
